import $ from 'jquery'

/**
 * Time inputs describe the form input for a time.
 */
class TimeInput {
  constructor(id) {
    this.id = id
    this.option = null
  }

  pull() {
    const time = $(`#${this.id}`).val()
    if (time !== '') {
      this.option = TimeInput.timeToSeconds(time)
    }
  }

  push() {
    if (this.option !== null && this.option !== '') {
      const time = TimeInput.secondsToTime(this.option)
      $(`#${this.id}`).val(time)
    }
  }

  /**
   * Convert a 24 hour format time into seconds passed midnight.
   */
  static timeToSeconds(time) {
    const parts = time.split(':')
    return parseInt(parts[0], 10)*3600 + parseInt(parts[1], 10)*60
  }

  /**
   * Convert seconds passed midnight into a 24 hour format time.
   */
  static secondsToTime(seconds) {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)

    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
  }
}

/**
 * Time range inputs describe a component that has a "from" and a "to" input.
 */
class TimeRangeInput {
  constructor(id, index = 0, fromValue = '', toValue = '') {
    this.id = id
    this.index = index
    this.from = new TimeInput(`${this.id}-from`)
    this.to = new TimeInput(`${this.id}-to`)
    this.rendered = false
  }

  /**
   * Compile the HTML and append it to an element.
   */
  render($el) {
    const $html = TimeRangeInput.makeHtml(
      this.id,
      this.index,
      this.from.id,
      this.to.id
    )
    $el.append($html)
    this.rendered = true
    this.push()
  }

  /**
   * Read values from the form and force them to be valid. '' is allowed.
   */
  pull() {
    this.from.pull()
    this.to.pull()

    const max = 86399
    const min = 0
    const to = isNaN(this.to.option) ? max : Math.min(this.to.option, max)
    const from = isNaN(this.from.option) ? min : Math.max(this.from.option, min)

    if (to < from) {
      this.to.option = this.from.option
      this.to.push()
    }
  }

  /**
   * Write the object values to the form.
   */
  push() {
    this.from.push()
    this.to.push()
  }

  /**
   * Copy the template and fill in some attributes.
   */
  static makeHtml(id, index, fromId, toId) {
    const $html = $($('#schedule-timerange-template')[0].outerHTML)
    const $inputs = $html.find('input.schedule-time')
    const $labels = $html.find('label.schedule-label')
    const $button = $html.find('button.schedule-delete')
    $html.attr('id', id)
    $($labels[0]).attr('for', fromId)
    $($inputs[0]).attr('id', fromId)
    $($labels[1]).attr('for', toId)
    $($inputs[1]).attr('id', toId)
    $button.attr('data-schedule-index', index)
    $html.attr('data-schedule-index', index)
    return $html[0].outerHTML
  }
}

/**
 * Manage the data in the schedule modal form.
 */
class ScheduleModel {
  constructor() {
    this.settingsHash = null
    this.currentSchedule = null
    this.currentType = null
    this.ranges = []
    this.types = null
    this.TYPE_ALL = 'all'
  }

  /**
   * Return a new array of the active_intervals settings for the current type.
   */
  get active() {
    let active = []
    try {
      active = this.config['active_intervals'].slice(0)
    }
    catch (e) {
      // Please forgive me.
    }

    return active
  }

  /**
   * Return the settings for the currently selected notification type.
   */
  get config() {
    let section = null
    try {
      section = this.settingsHash[this.currentType]
    }
    catch {
      // Please forgive me.
    }

    return section
  }

  /**
   * Return an array of the current schedule.
   */
  get intervals() {
    return this.currentSchedule !== null ? this.currentSchedule : []
  }

  /**
   * Return a new array of the full settings ready to send to the API.
   */
  get notificationSettings() {
    const activeSettings = Object.values(this.settingsHash)
    const settings = JSON.parse(JSON.stringify(activeSettings))
    const all = this.currentType === this.TYPE_ALL
    for (let i = 0; i < this.types.length; i++) {
      if (all || (settings[i]['type'] === this.currentType)) {
        settings[i]['active_intervals'] = this.intervals
      }
      settings[i] = this.cleanupConfig(settings[i])
    }

    return settings
  }

  /**
   * Add an interval and refresh the view.
   */
  addInterval() {
    this.currentSchedule.push({
      start: '',
      end: '',
    })
    this.push()
  }

  /**
   * Remove an interval from currentSchedule by index and refresh the view.
   */
  removeInterval(index) {
    if (this.currentSchedule !== null && this.currentSchedule.length > index) {
      this.currentSchedule.splice(index, 1)
      this.push()
    }
  }

  /**
   * Sync the currentSchedule with the form values for a range by index.
   */
  setInterval(index) {
    if (this.ranges.length > index) {
      this.ranges[index].pull()
      this.currentSchedule[index] = {
        start: this.ranges[index].from.option,
        end: this.ranges[index].to.option
      }
    }
  }

  /**
   * Display the schedule forms for another type.
   */
  changeType(type) {
    if (type !== this.TYPE_ALL && this.currentType !== this.TYPE_ALL) {
      this.currentType = type
      this.currentSchedule = this.active
    }
    this.currentType = type
    this.push()
  }

  /**
   * Return a new array with overlapping intervals combined.
   */
  flatten(intervals) {
    const ordered = this.sort(intervals)
    const flat = []
    for (let i = 0; i < ordered.length; i++) {
      if (ordered[i].start >= ordered[i].end) {
        continue
      }

      const j = i + 1;
      const next = j < ordered.length ? ordered[j] : null
      if (next && next.start <= ordered[i].end) {
        // The next interval starts within this interval so merge them. Use the
        // latest end time then delete the next interval and step back one
        // iteration so this interval is compared to the next next interval,
        // i.e. k = j + 1 = i + 2.
        if (next.end > ordered[i].end) {
          ordered[i].end = next.end
        }
        ordered.splice(j, 1)
        i -= 1
        continue
      }

      flat.push({
        start: ordered[i].start,
        end: ordered[i].end
      })

    }

    return flat
  }

  /**
   * Return the list of configurations as a new object keyed by type.
   */
  hashSettings(configs) {
    const configsCopy = JSON.parse(JSON.stringify(configs))
    const settingsHash = {}
    for (const config of configsCopy) {
      settingsHash[config.type] = config
    }

    return settingsHash
  }

  /**
   * Return true if the settings for this alert have change. False otherwise.
   */
  isChanged() {
    if (this.currentType === this.TYPE_ALL) {
      return false
    }
    const current = this.currentSchedule

    return JSON.stringify(current) !== JSON.stringify(this.active)
  }

  /**
   * Set the model data from the form values.
   */
  pull() {
    for (let i = 0; i < this.intervals.length; i++) {
      this.setInterval(i)
    }
  }

  /**
   * Set the form values from the model data.
   */
  push() {
    this.ranges = []
    for (let i = 0; i < this.intervals.length; i++) {
      const interval = this.intervals[i]
      const input = new TimeRangeInput(`schedule-range-${i}`, i)
      input.from.option = interval['start']
      input.to.option = interval['end']
      this.ranges[i] = input
    }
    this.render()
  }

  /**
   * Set the model data from a config object.
   */
  load(configs, type = null) {
    this.settingsHash = this.hashSettings(configs)
    const options = $('#schedule-notification-type option')
    const types = options.toArray().map(option => $(option).val())
    // const types = Object.keys(this.settingsHash)
    // types.sort()
    this.types = types
    this.changeType(type ?? this.types[0] ?? this.TYPE_ALL)
  }

  /**
   * Clear the HTML and append each range.
   */
  render() {
    const all = this.currentType === this.TYPE_ALL
    const changed = this.isChanged()
    const empty = this.ranges.length === 0
    const $div = $('#schedule-intervals').html('')

    for (const input of this.ranges) {
      input.render($div)
    }

    ! empty || all || changed ?
      $('#schedule-intervals-empty').hide() :
      $('#schedule-intervals-empty').show()
    empty && all ?
      $('#schedule-intervals-empty-all').show() :
      $('#schedule-intervals-empty-all').hide()
    ! (empty || all || changed) ?
      $('#schedule-intervals-exists').show() :
      $('#schedule-intervals-exists').hide()
    ! empty && all ?
      $('#schedule-intervals-all').show() :
      $('#schedule-intervals-all').hide()
    changed && ! all && ! empty ?
      $('#schedule-intervals-changed').show() :
      $('#schedule-intervals-changed').hide()
    changed && ! all && empty ?
      $('#schedule-intervals-empty-changed').show() :
      $('#schedule-intervals-empty-changed').hide()
    changed ?
      $('#schedule-reset').show() :
      $('#schedule-reset').hide()
  }

  /**
   * Remove the current schedule.
   */
  clear() {
    this.currentSchedule = []
    this.push()
  }

  /**
   * Reset changes to the schedule for the selected notification type.
   */
  reset() {
    this.currentSchedule = this.active
    this.push()
  }

  /**
   * Return a new array sorted by interval start time.
   */
  sort(intervals) {
    const ordered = intervals.slice(0)
    ordered.sort((a, b) => a.start - b.start)

    return ordered
  }

  /**
   * Flatten intervals or delete active_intervals from config if there are none.
   */
  cleanupConfig(config) {
    let active = null
    if (config['active_intervals']) {
      active = config['active_intervals']
    }
    if (active && active.length > 0) {
        config['active_intervals'] = this.flatten(active)
    }
    else {
      delete config['active_intervals']
    }

    return config
  }

}

export { ScheduleModel }
